import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'
import { Smartphone, Monitor, Plus, X, RefreshCw } from 'lucide-react'

const DeviceAdministrationContent = () => {
  const [activeTab, setActiveTab] = useState('mobile')
  const [mobileDevices, setMobileDevices] = useState([])
  const [desktopDevices, setDesktopDevices] = useState([])
  const [mobileModalState, setMobileModalState] = useState(null)
  const [desktopModalState, setDesktopModalState] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Device synchronization states
  const [syncModalState, setSyncModalState] = useState(null) // 'process', 'result', or null
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncResult, setSyncResult] = useState(null)

  // Pagination states
  const [mobilePagination, setMobilePagination] = useState({
    page: 1,
    page_size: 25,
    total: 0,
    total_pages: 0
  })
  const [desktopPagination, setDesktopPagination] = useState({
    page: 1,
    page_size: 25,
    total: 0,
    total_pages: 0
  })

  const mobileColumns = useMemo(() => ([
    { key: 'device_type', label: 'Device Type' },
    { key: 'device_name', label: 'Device Name' },
    { key: 'operating_system', label: 'Operating System' }
  ]), [])

  const desktopColumns = useMemo(() => ([
    { key: 'device_type', label: 'Device Type' },
    { key: 'device_name', label: 'Device Name' },
    { key: 'processor', label: 'Processor' },
    { key: 'screen_size', label: 'Screen Size' },
    { key: 'release_year', label: 'Release Year' }
  ]), [])

  // Fetch devices from API with pagination
  const fetchDevices = async (searchTerm = '', mobilePageOverride = null, desktopPageOverride = null) => {
    setLoading(true)
    setError('')
    try {
      const mobileCurrentPage = mobilePageOverride || mobilePagination.page
      const desktopCurrentPage = desktopPageOverride || desktopPagination.page

      const mobileParams = new URLSearchParams({
        page: mobileCurrentPage.toString(),
        page_size: '25'
      })
      const desktopParams = new URLSearchParams({
        page: desktopCurrentPage.toString(),
        page_size: '25'
      })

      if (searchTerm) {
        mobileParams.append('search', searchTerm)
        desktopParams.append('search', searchTerm)
      }

      const [mobileResponse, desktopResponse] = await Promise.all([
        apiFetch(`/api/admin/mobile-devices?${mobileParams.toString()}`),
        apiFetch(`/api/admin/desktop-devices?${desktopParams.toString()}`)
      ])

      if (mobileResponse.ok && desktopResponse.ok) {
        const mobileData = await mobileResponse.json()
        const desktopData = await desktopResponse.json()

        setMobileDevices(mobileData.mobile_devices || [])
        setDesktopDevices(desktopData.desktop_devices || [])
        setMobilePagination(mobileData.pagination || { page: 1, page_size: 25, total: 0, total_pages: 0 })
        setDesktopPagination(desktopData.pagination || { page: 1, page_size: 25, total: 0, total_pages: 0 })
      } else {
        throw new Error('Failed to fetch devices')
      }
    } catch (err) {
      setError('Failed to load devices: ' + err.message)
      console.error('Error fetching devices:', err)
    } finally {
      setLoading(false)
    }
  }

  // Load devices on component mount
  useEffect(() => {
    void fetchDevices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle search with debouncing - reset to page 1
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setMobilePagination(prev => ({ ...prev, page: 1 }))
      setDesktopPagination(prev => ({ ...prev, page: 1 }))
      void fetchDevices(searchQuery, 1, 1)
    }, 300)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  // Pagination handlers
  const handleMobilePage = (newPage) => {
    setMobilePagination(prev => ({ ...prev, page: newPage }))
    void fetchDevices(searchQuery, newPage, null)
  }

  const handleDesktopPage = (newPage) => {
    setDesktopPagination(prev => ({ ...prev, page: newPage }))
    void fetchDevices(searchQuery, null, newPage)
  }

  const openAddModal = (type) => {
    if (type === 'mobile') {
      setMobileModalState({ mode: 'add', device: null })
    } else {
      setDesktopModalState({ mode: 'add', device: null })
    }
  }

  const openEditModal = (type, device) => {
    if (type === 'mobile') {
      setMobileModalState({ mode: 'edit', device })
    } else {
      setDesktopModalState({ mode: 'edit', device })
    }
  }

  const closeModal = () => {
    setMobileModalState(null)
    setDesktopModalState(null)
  }

  const handleSaveMobile = async (device) => {
    setLoading(true)
    try {
      const method = mobileModalState?.mode === 'edit' ? 'PUT' : 'POST'
      const url = mobileModalState?.mode === 'edit'
        ? `/api/admin/mobile-devices/${encodeURIComponent(mobileModalState.device.device_type)}`
        : '/api/admin/mobile-devices'

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(device)
      })

      if (response.ok) {
        await fetchDevices(searchQuery)
        closeModal()
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save mobile device')
      }
    } catch (err) {
      setError('Failed to save mobile device: ' + err.message)
      console.error('Error saving mobile device:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveDesktop = async (device) => {
    setLoading(true)
    try {
      const method = desktopModalState?.mode === 'edit' ? 'PUT' : 'POST'
      const url = desktopModalState?.mode === 'edit'
        ? `/api/admin/desktop-devices/${encodeURIComponent(desktopModalState.device.device_type)}`
        : '/api/admin/desktop-devices'

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...device,
          release_year: Number.parseInt(device.release_year, 10)
        })
      })

      if (response.ok) {
        await fetchDevices(searchQuery)
        closeModal()
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to save desktop device')
      }
    } catch (err) {
      setError('Failed to save desktop device: ' + err.message)
      console.error('Error saving desktop device:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return

    setLoading(true)
    try {
      const deviceType = encodeURIComponent(deleteTarget.device.device_type)
      const url = deleteTarget.type === 'mobile'
        ? `/api/admin/mobile-devices/${deviceType}`
        : `/api/admin/desktop-devices/${deviceType}`

      const response = await apiFetch(url, { method: 'DELETE' })

      if (response.ok) {
        await fetchDevices(searchQuery)
        setDeleteTarget(null)
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete device')
      }
    } catch (err) {
      setError('Failed to delete device: ' + err.message)
      console.error('Error deleting device:', err)
    } finally {
      setLoading(false)
    }
  }

  // Device synchronization handler
  const handleDeviceSync = async () => {
    setSyncModalState('process')
    setSyncLoading(true)
    setSyncResult(null)

    try {
      const response = await apiFetch('/api/admin/device-sync', {
        method: 'POST'
      })

      const data = await response.json()

      if (response.ok) {
        setSyncResult({
          success: true,
          message: data.message,
          result: data.result
        })
        // Refresh device lists after sync
        await fetchDevices(searchQuery)
      } else {
        setSyncResult({
          success: false,
          message: data.error || 'Device synchronization failed'
        })
      }
    } catch (err) {
      setSyncResult({
        success: false,
        message: 'Network error during device synchronization: ' + err.message
      })
      console.error('Error during device sync:', err)
    } finally {
      setSyncLoading(false)
      setSyncModalState('result')
    }
  }

  const activeDevices = activeTab === 'mobile' ? mobileDevices : desktopDevices
  const columns = activeTab === 'mobile' ? mobileColumns : desktopColumns
  const activePagination = activeTab === 'mobile' ? mobilePagination : desktopPagination
  const handlePageChange = activeTab === 'mobile' ? handleMobilePage : handleDesktopPage

  return (
    <div className="content-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Devices</h2>
        <div className="search-container" style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search devices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #374151',
              backgroundColor: '#1f2937',
              color: '#fff',
              width: '250px'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#dc2626',
          color: 'white',
          borderRadius: '6px'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div className="device-tabs">
        <button
          type="button"
          className={`device-tab ${activeTab === 'mobile' ? 'active' : ''}`}
          onClick={() => setActiveTab('mobile')}
        >
          Mobile Devices ({mobilePagination.total})
        </button>
        <button
          type="button"
          className={`device-tab ${activeTab === 'desktop' ? 'active' : ''}`}
          onClick={() => setActiveTab('desktop')}
        >
          Desktop Devices ({desktopPagination.total})
        </button>
      </div>

      <div className="user-controls">
        <button
          className="btn-primary"
          onClick={() => openAddModal(activeTab)}
          disabled={loading}
        >
          Add {activeTab === 'mobile' ? 'Mobile Device' : 'Desktop Device'}
        </button>
        <button
          className="btn-primary"
          onClick={handleDeviceSync}
          disabled={loading || syncLoading}
          style={{
            marginLeft: '12px',
            backgroundColor: '#10b981',
            borderColor: '#10b981'
          }}
        >
          {syncLoading ? 'Synchronizing...' : (
            <>
              <RefreshCw size={16} style={{marginRight: '6px'}} />
              Sync Devices
            </>
          )}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          Loading devices...
        </div>
      ) : (
        <DeviceTable
          devices={activeDevices}
          columns={columns}
          primaryKey="device_type"
          onEdit={(device) => openEditModal(activeTab, device)}
          onDelete={(device) => setDeleteTarget({ type: activeTab, device })}
          searchQuery={searchQuery}
        />
      )}

      {/* Pagination */}
      {!loading && activePagination.total_pages > 1 && (
        <Pagination
          currentPage={activePagination.page}
          totalPages={activePagination.total_pages}
          onPageChange={handlePageChange}
          total={activePagination.total}
          pageSize={activePagination.page_size}
        />
      )}

      {mobileModalState && (
        <DeviceForm
          title={mobileModalState.mode === 'add' ? 'Add Mobile Device' : 'Edit Mobile Device'}
          fields={[
            { name: 'device_type', label: 'Device Type', disabled: mobileModalState.mode === 'edit' },
            { name: 'device_name', label: 'Device Name' },
            { name: 'operating_system', label: 'Operating System' }
          ]}
          initialValues={mobileModalState.device || { device_type: '', device_name: '', operating_system: '' }}
          validate={(values) => {
            if (!values.device_type.trim() || !values.device_name.trim() || !values.operating_system.trim()) {
              return 'Please fill in all fields.'
            }
            if (values.device_type.includes(' ')) {
              return 'Device Type should not contain spaces.'
            }
            return null
          }}
          onSave={handleSaveMobile}
          onCancel={closeModal}
          loading={loading}
        />
      )}

      {desktopModalState && (
        <DeviceForm
          title={desktopModalState.mode === 'add' ? 'Add Desktop Device' : 'Edit Desktop Device'}
          fields={[
            { name: 'device_type', label: 'Device Type', disabled: desktopModalState.mode === 'edit' },
            { name: 'device_name', label: 'Device Name' },
            { name: 'processor', label: 'Processor' },
            { name: 'screen_size', label: 'Screen Size' },
            { name: 'release_year', label: 'Release Year', type: 'number' }
          ]}
          initialValues={desktopModalState.device || { device_type: '', device_name: '', processor: '', screen_size: '', release_year: '' }}
          validate={(values) => {
            if (!values.device_type.trim() || !values.device_name.trim() || !values.processor.trim() || !values.screen_size.trim() || !String(values.release_year).trim()) {
              return 'Please fill in all fields.'
            }
            if (values.device_type.includes(' ')) {
              return 'Device Type should not contain spaces.'
            }
            const releaseYearNumber = Number(values.release_year)
            if (Number.isNaN(releaseYearNumber) || releaseYearNumber < 1970 || releaseYearNumber > new Date().getFullYear() + 1) {
              return 'Enter a valid release year.'
            }
            return null
          }}
          onSave={handleSaveDesktop}
          onCancel={closeModal}
          loading={loading}
        />
      )}

      {deleteTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Delete Device</h3>
              <button className="close-btn" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              Are you sure you want to delete the {deleteTarget.type === 'mobile' ? 'mobile' : 'desktop'} device "{deleteTarget.device.device_name}"?
            </p>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setDeleteTarget(null)} disabled={loading}>
                Cancel
              </button>
              <button type="button" className="btn-delete" onClick={handleDelete} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Synchronization Process Modal */}
      {syncModalState === 'process' && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Device Synchronization</h3>
            </div>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '20px auto', width: '40px', height: '40px' }}></div>
              <p style={{ color: '#e5e7eb', marginTop: '16px' }}>
                Synchronizing devices with OpenAI...
              </p>
              <p style={{ color: '#9ca3af', fontSize: '0.9em' }}>
                This may take a moment. Please wait.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Device Synchronization Result Modal */}
      {syncModalState === 'result' && syncResult && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Synchronization {syncResult.success ? 'Complete' : 'Failed'}</h3>
              <button className="close-btn" onClick={() => setSyncModalState(null)}>×</button>
            </div>
            <div style={{ padding: '20px' }}>
              {syncResult.success ? (
                <div>
                  <div style={{
                    color: '#10b981',
                    fontSize: '48px',
                    textAlign: 'center',
                    marginBottom: '16px'
                  }}>
                    ✅
                  </div>
                  <p style={{ color: '#e5e7eb', marginBottom: '16px', textAlign: 'center' }}>
                    {syncResult.message}
                  </p>
                  {syncResult.result && (
                    <div style={{
                      backgroundColor: '#1f2937',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #374151'
                    }}>
                      <h4 style={{ color: '#e5e7eb', marginBottom: '12px' }}>Results:</h4>
                      <ul style={{ color: '#d1d5db', fontSize: '0.9em', listStyle: 'none', padding: 0 }}>
                        <li><Smartphone size={14} style={{display: 'inline', marginRight: '4px'}} /> Missing Mobile Devices: {syncResult.result.missing_mobile_count}</li>
                        <li><Monitor size={14} style={{display: 'inline', marginRight: '4px'}} /> Missing Desktop Devices: {syncResult.result.missing_desktop_count}</li>
                        <li><Plus size={14} style={{display: 'inline', marginRight: '4px'}} /> Mobile Devices Added: {syncResult.result.added_mobile_count}</li>
                        <li><Plus size={14} style={{display: 'inline', marginRight: '4px'}} /> Desktop Devices Added: {syncResult.result.added_desktop_count}</li>
                        <li><X size={14} style={{display: 'inline', marginRight: '4px'}} /> Errors: {syncResult.result.error_count}</li>
                      </ul>
                      {syncResult.result.added_mobile_devices?.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <h5 style={{ color: '#10b981', marginBottom: '4px' }}>Added Mobile Devices:</h5>
                          {syncResult.result.added_mobile_devices.map(d => (
                            <div key={d.device_type} style={{ paddingLeft: '8px', fontSize: '0.85em', color: '#d1d5db' }}>
                              {d.device_name} ({d.device_type})
                            </div>
                          ))}
                        </div>
                      )}
                      {syncResult.result.added_desktop_devices?.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <h5 style={{ color: '#10b981', marginBottom: '4px' }}>Added Desktop Devices:</h5>
                          {syncResult.result.added_desktop_devices.map(d => (
                            <div key={d.device_type} style={{ paddingLeft: '8px', fontSize: '0.85em', color: '#d1d5db' }}>
                              {d.device_name} ({d.device_type})
                            </div>
                          ))}
                        </div>
                      )}
                      {syncResult.result.errors && syncResult.result.errors.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <h5 style={{ color: '#ef4444', marginBottom: '8px' }}>Errors:</h5>
                          <div style={{
                            backgroundColor: '#dc2626',
                            color: 'white',
                            padding: '8px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            maxHeight: '100px',
                            overflowY: 'auto'
                          }}>
                            {syncResult.result.errors.map((error) => (
                              <div key={error}>{error}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{
                    color: '#ef4444',
                    fontSize: '48px',
                    textAlign: 'center',
                    marginBottom: '16px'
                  }}>
                    ❌
                  </div>
                  <div style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    {syncResult.message}
                  </div>
                </div>
              )}
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setSyncModalState(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const DeviceTable = ({ devices, columns, onEdit, onDelete, primaryKey, searchQuery }) => {
  return (
    <div className="users-table">
      <table>
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column.key}>{column.label}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {devices.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} style={{ textAlign: 'center', color: '#9ca3af' }}>
                {searchQuery ? `No devices found matching "${searchQuery}"` : 'No devices available'}
              </td>
            </tr>
          ) : (
            devices.map(device => (
              <tr key={device[primaryKey]}>
                {columns.map(column => (
                  <td key={column.key}>{device[column.key]}</td>
                ))}
                <td>
                  <button className="btn-edit" onClick={() => onEdit(device)}>Edit</button>
                  <button className="btn-delete" onClick={() => onDelete(device)}>Delete</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

DeviceTable.propTypes = {
  devices: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  primaryKey: PropTypes.string.isRequired,
  searchQuery: PropTypes.string.isRequired
}

const DeviceForm = ({ title, fields, initialValues, onSave, onCancel, validate, loading }) => {
  const [values, setValues] = useState(initialValues)
  const [error, setError] = useState('')

  useEffect(() => {
    setValues(initialValues)
    setError('')
  }, [initialValues])

  const handleChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    const validationError = validate(values)
    if (validationError) {
      setError(validationError)
      return
    }
    onSave(values)
  }

  return (
    <div className="user-form-overlay">
      <div className="user-form-container">
        <div className="user-form-header">
          <h3>{title}</h3>
          <button className="close-btn" onClick={onCancel} disabled={loading}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="user-form">
          {fields.map(field => (
            <div className="form-group" key={field.name}>
              <label htmlFor={field.name}>{field.label}</label>
              <input
                id={field.name}
                type={field.type || 'text'}
                value={values[field.name] ?? ''}
                onChange={(event) => handleChange(field.name, event.target.value)}
                disabled={field.disabled || loading}
              />
            </div>
          ))}
          {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

DeviceForm.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
}

const Pagination = ({ currentPage, totalPages, onPageChange, total, pageSize }) => {
  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)

    if (end - start + 1 < maxVisible && start > 1) {
      start = Math.max(1, end - maxVisible + 1)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    return pages
  }

  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, total)

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '20px',
      padding: '16px',
      background: 'rgba(45, 45, 45, 0.95)',
      borderRadius: '12px',
      border: '1px solid rgba(79, 70, 229, 0.2)'
    }}>
      <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
        Showing {startItem} to {endItem} of {total} devices
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            background: currentPage === 1 ? 'rgba(107, 114, 128, 0.3)' : 'rgba(79, 70, 229, 0.8)',
            color: currentPage === 1 ? '#6b7280' : 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          Previous
        </button>

        {getPageNumbers().map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              padding: '8px 12px',
              background: page === currentPage ? 'rgba(79, 70, 229, 1)' : 'rgba(79, 70, 229, 0.2)',
              color: page === currentPage ? 'white' : '#e5e7eb',
              border: '1px solid rgba(79, 70, 229, 0.3)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: page === currentPage ? '600' : '400'
            }}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            background: currentPage === totalPages ? 'rgba(107, 114, 128, 0.3)' : 'rgba(79, 70, 229, 0.8)',
            color: currentPage === totalPages ? '#6b7280' : 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired
}

export default DeviceAdministrationContent